import { isUnknown } from "generic-type-guard";
import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeLeading } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { completeTaskReconciliationAttempts } from "../../../config";
import { saveActivity } from "../../../utilities/activityUtils";
import { completeTask, TaskCompletionOutcomeUnknownError } from "../../../utilities/rpcHelper";
import { isMap } from "../../../utilities/typeGuardUtils";
import {
	isDocumentContainingA12wf,
	pullWfDocumentWithMetadata,
	storeReconciledCompletionState
} from "../../../utilities/workflowUtils";

import { customCompleteTask } from "../actions/workflowsActions";
import { isDocumentWithProcessMetadata } from "../data/processMetadata";

const documentService = new DocumentServiceFactory().getDocumentService();
const completionFailedErrorName = "complete-task-failed";
const completionOutcomeUnknownErrorName = "complete-task-outcome-unknown";
const logger = LoggerFactory.getLogger("complete-task-saga");

export function* customCompleteTaskSaga(): SagaIterator<void> {
	yield* takeLeading(
		customCompleteTask,
		function* handleOpenDocument(action: Action<WorkflowsActions.CompleteTaskPayload>): SagaGenerator<void> {
			const documentModel = yield* select(
				ModelSelectors.modelInScene(
					{ activityId: action.payload.activityId, modelType: "document" },
					Model.isDocumentModel
				)
			);

			if (!documentModel) {
				yield* call(completeTaskFailed, action.payload, completionFailedErrorName);
				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { modelId, id, ...documentWithoutModelAndDocumentId } = action.payload.document;
			const parsedDocument = documentService.formatDates(documentWithoutModelAndDocumentId, documentModel);

			if (!isMap(isUnknown)(parsedDocument)) {
				yield* call(completeTaskFailed, action.payload, completionFailedErrorName);
				return;
			}

			const activityId = action.payload.activityId;

			const isSaved = yield* call(saveActivity, activityId);
			if (!isSaved) {
				return;
			}

			if (!isDocumentContainingA12wf(parsedDocument)) {
				yield* call(completeTaskFailed, action.payload, completionFailedErrorName);
				return;
			}

			if (!isDocumentWithProcessMetadata(parsedDocument)) {
				yield* call(completeTaskFailed, action.payload, completionFailedErrorName);
				return;
			}

			const oldTaskId = parsedDocument.A12WF.Task.ID;
			let taskCompletionConfirmed = false;
			let completionOutcomeUnknown = false;

			try {
				yield* call(completeTask, action.payload.docRef);
				taskCompletionConfirmed = true;
			} catch (error) {
				logger.error(error);
				if (error instanceof TaskCompletionOutcomeUnknownError) {
					completionOutcomeUnknown = true;
					try {
						const serverState = yield* call(
							pullWfDocumentWithMetadata,
							action.payload.docRef,
							oldTaskId,
							completeTaskReconciliationAttempts
						);
						taskCompletionConfirmed = Boolean(serverState);
						if (serverState) {
							yield* call(
								storeReconciledCompletionState,
								action.payload.activityId,
								action.payload.docRef,
								serverState
							);
						}
					} catch (reconciliationError) {
						logger.error(reconciliationError);
					}
				}
			}

			if (taskCompletionConfirmed) {
				yield* put(
					WorkflowsActions.completeTask.done({
						params: action.payload,
						result: {}
					})
				);
			} else {
				yield* call(
					completeTaskFailed,
					action.payload,
					completionOutcomeUnknown ? completionOutcomeUnknownErrorName : completionFailedErrorName
				);
			}
		}
	);
}

function* completeTaskFailed(payload: WorkflowsActions.CompleteTaskPayload, name: string): SagaGenerator<void> {
	yield* put(
		WorkflowsActions.completeTask.failed({
			params: payload,
			error: { name, message: name }
		})
	);
	yield* put(ApplicationActions.setBusy(false));
}
